import { isLocale, type Locale } from '@platform/i18n';
import type { LocationRewrite, RouterHistory } from '@tanstack/react-router';

const PATHS_WITHOUT_LOCALE: ReadonlySet<string> = new Set(['/auth/callback']);

const firstSegmentOf = (pathname: string): string => pathname.split('/')[1] ?? '';

export const localeOfPath = (pathname: string): Locale | null => {
  const segment = firstSegmentOf(pathname);
  return isLocale(segment) ? segment : null;
};

export const startingLocale = (pathname: string, chosen: Locale | null, fallback: Locale): Locale =>
  localeOfPath(pathname) ?? chosen ?? fallback;

export const withoutLocale = (pathname: string): string => {
  const locale = localeOfPath(pathname);
  if (locale === null) return pathname;
  return pathname.slice(locale.length + 1) || '/';
};

export const withLocale = (pathname: string, locale: Locale): string => {
  const internal = withoutLocale(pathname);
  if (PATHS_WITHOUT_LOCALE.has(internal)) return internal;
  return internal === '/' ? `/${locale}` : `/${locale}${internal}`;
};

export const withoutLocaleInHref = (href: string): string => {
  const queryStart = href.search(/[?#]/);
  if (queryStart === -1) return withoutLocale(href);
  return `${withoutLocale(href.slice(0, queryStart))}${href.slice(queryStart)}`;
};

const rewritingPath =
  (rewrite: (pathname: string) => string) =>
  ({ url }: { url: URL }): URL => {
    const next = new URL(url.href);
    next.pathname = rewrite(url.pathname);
    return next;
  };

export const localeRewrite = (currentLocale: () => Locale): LocationRewrite => ({
  input: rewritingPath(withoutLocale),
  output: rewritingPath((pathname) => withLocale(pathname, currentLocale())),
});

export const showLocaleInUrl = (history: RouterHistory, locale: Locale): void => {
  const { pathname, search, hash, state } = history.location;
  const localized = withLocale(pathname, locale);
  if (localized === pathname) return;
  history.replace(`${localized}${search}${hash}`, state, { ignoreBlocker: true });
};
